/**
 * A better polling and update handler.
 * notify.js is too complex for simple modifications without side effects.
 *
 * The usage for this class is very simple:
 *
 *    - 1. Poll a given url periodically.
 *    - 2. The response must be an array of {} with the property 'modified_timestamp'.
 *    - 3. Keep a cache of all results, adding the 'new_' attribute to new values.
 *    - 4. If any new values are found, trigger a callback(poll_instance)
 *
 * The only external api is 'clear', which allows the caller to mark all
 * 'new' items as no longer new. This sets the 'new_' field to false.
 *
 * @param url The url to poll for changes.
 * @param interval The interval in ms to poll the url with.
 * @param callback The callback to invoke when any new objects are found.
 */
var Poll = function (url, interval, callback) {
  this._url = url;
  this._interval = interval;
  this._callback = callback;
  this._last_clear = this._timestamp();
  this._timer = null;

  /**
   * Public property: the set of loaded items.
   * Any 'new' item will be marked with 'new' = true.
   */
  this.data = [];

  /**
   * The count of new items in the data for summary views
   */
  this.new_ = 0;

  /**
   * The timestamp of the oldest new item.
   */
  this.oldestNewItem = null;

  /**
   * Public property: Is the instance currently polling.
   * This is always true unless an error has occurred.
   */
  this.active = true;

  /**
   * Public property: The last error or null.
   * The format of an error is { 'reason': '...', 'context': ... }
   */
  this.error = null;

  // Start polling
  this._poll();
  this._query();
};

/**
 * Clear the current set of 'new_' flags.
 * Note this doesn't clear data, just the flags.
 */
Poll.prototype.clear = function (key) {
  this._last_clear = this._timestamp();
  this._remap();
  this._change();
};

/** Start polling for changes */
Poll.prototype._poll = function () {
  var self = this;
  this._timer = setInterval(function () {
    self._query();
  }, this._interval);
};

/** Poll for changes */
Poll.prototype._query = function () {
  var self = this;
  $.ajax(self._url + '?ie_cachebust=' + self._timestamp()).then(function (data, textStatus, jqXHR) {
    self.data = data;
    self._remap();
  }, function (jqXHR, textStatus, errorThrown) {
    self._fail('api query failed: ' + errorThrown + ', status: ' + jqXHR.status, jqXHR);
  });
};

/** Remap the data object adding new tags if required */
Poll.prototype._remap = function () {
  var self = this;
  var changed = false;

  var is_new = function (item) {
    return item.modified_timestamp > self._last_clear;
  };
  self.new_ = 0;
  self.oldestNewItem = 0;
  $.each(this.data, function (i, e) {
    if (e.new_ === undefined) {
      e.new_ = false;
    }
    var tmp = e.new_;
    e.new_ = is_new(e);
    if (e.new_) {
      ++self.new_;
      if (!self.oldestNewItem) {
        self.oldestNewItem = e;
      } else if (e.modified_timestamp < self.oldestNewItem.modified_timestamp) {
        self.oldestNewItem = e;
      }
    }
    if (tmp !== e.new_) {
      changed = true;
    }
  });
  if (changed) {
    this._change();
  }
};

/** Trigger an update for an external change */
Poll.prototype._change = function () {
  if (this._callback) {
    var self = this;
    setTimeout(function () {
      self._callback(self);
    }, 1);
  }
};

/**
 * Generate a new UTC timestamp for right now
 */
Poll.prototype._timestamp = function () {
  return ~~(+new Date() / 1000);
};

/**
 * Fail because of reasons.
 * @param reason The reason we failed in readable text.
 * @param context Any contextual data for debugging.
 */
Poll.prototype._fail = function (reason, context) {
  this.active = false;
  this.error = {
    reason: reason,
    context: context
  };
  throw new Error(reason);
};

export default Poll;